import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
	ClipboardList,
	FileText,
	MessageSquare,
	CheckCircle,
	TrendingUp,
	Plus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@hiring-intelligence/ui';
import { Button } from '@hiring-intelligence/ui';
import { hiringApi, sourcingApi } from '@/services/api';
import { formatDisplayNumber } from '@hiring-intelligence/shared';

export default function DashboardPage() {
	const { data: hiringData } = useQuery({
		queryKey: ['hiring-requests', { page: 1, limit: 5 }],
		queryFn: () => hiringApi.list(1, 5),
		refetchOnWindowFocus: false
	});

	const { data: statsData } = useQuery({
		queryKey: ['hiring-request-stats'],
		queryFn: () => hiringApi.stats(),
		refetchOnWindowFocus: false
	});

	const { data: budgetData } = useQuery({
		queryKey: ['signalhire-credits'],
		queryFn: () => sourcingApi.credits(),
		refetchOnWindowFocus: false
	});

	const stats = [
		{
			name: 'Active Requests',
			value: statsData?.data?.active || 0,
			icon: ClipboardList,
			color: 'text-blue-500',
			bgColor: 'bg-blue-500/10'
		},
		{
			name: 'Draft JDs',
			value: statsData?.data?.draftJds || 0,
			icon: FileText,
			color: 'text-orange-500',
			bgColor: 'bg-orange-500/10'
		},
		{
			name: 'Pending Feedback',
			value: statsData?.data?.pendingFeedback || 0,
			icon: MessageSquare,
			color: 'text-purple-500',
			bgColor: 'bg-purple-500/10'
		},
		{
			name: 'Completed',
			value: statsData?.data?.completed || 0,
			icon: CheckCircle,
			color: 'text-green-500',
			bgColor: 'bg-green-500/10'
		}
	];

	const getStatusBadge = (status: string) => {
		const styles: Record<string, string> = {
			draft: 'bg-gray-100 text-gray-700',
			gathering_criteria: 'bg-blue-100 text-blue-700',
			criteria_complete: 'bg-indigo-100 text-indigo-700',
			jd_generated: 'bg-orange-100 text-orange-700',
			pending_feedback: 'bg-purple-100 text-purple-700',
			hiring: 'bg-teal-100 text-teal-700',
			completed: 'bg-green-100 text-green-700',
			cancelled: 'bg-red-100 text-red-700'
		};
		const labels: Record<string, string> = {
			draft: 'Draft',
			gathering_criteria: 'Gathering Criteria',
			criteria_complete: 'Ready for Sourcing',
			jd_generated: 'JD Generated',
			pending_feedback: 'Pending Feedback',
			hiring: 'Hiring',
			completed: 'Completed',
			cancelled: 'Cancelled'
		};
		return (
			<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
				{labels[status] || status}
			</span>
		);
	};

	return (
		<div className="p-4 md:p-8">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
				<div>
					<h1 className="text-2xl font-bold">Dashboard</h1>
					<p className="text-muted-foreground">Overview of your hiring activities</p>
				</div>
				<Button asChild>
					<Link to="/companies">
						<Plus className="mr-2 h-4 w-4" />
						New Hiring Request
					</Link>
				</Button>
			</div>

			{/* Stats */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
				{stats.map((stat) => (
					<Card key={stat.name}>
						<CardContent className="p-6">
							<div className="flex items-center gap-4">
								<div className={`p-3 rounded-lg ${stat.bgColor}`}>
									<stat.icon className={`h-6 w-6 ${stat.color}`} />
								</div>
								<div>
									<p className="text-2xl font-bold">{formatDisplayNumber(stat.value)}</p>
									<p className="text-sm text-muted-foreground">{stat.name}</p>
								</div>
							</div>
						</CardContent>
					</Card>
				))}
			</div>

			{/* Search Credits - hidden */}

			{/* Recent Requests */}
			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<CardTitle>Recent Hiring Requests</CardTitle>
					{hiringData?.data?.length ? (
						<Link to="/hiring-requests" className="text-sm font-medium text-primary hover:underline">
							See All
						</Link>
					) : null}
				</CardHeader>
				<CardContent>
					{hiringData?.data?.length ? (
						<div className="space-y-4">
							{hiringData.data.map((request: any) => (
								<Link
									key={request.id}
									to={`/hiring-requests/${request.id}`}
									className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 rounded-lg border hover:bg-accent transition-colors"
								>
									<div>
										<p className="font-medium">{request.roleTitle}</p>
										<p className="text-sm text-muted-foreground">
											{request.company?.name || 'No company'}
										</p>
									</div>
									{getStatusBadge(request.status)}
								</Link>
							))}
						</div>
					) : (
						<div className="text-center py-8 text-muted-foreground">
							<ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
							<p>No hiring requests yet</p>
							<Button asChild variant="link" className="mt-2">
								<Link to="/hiring-requests">Create your first request</Link>
							</Button>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
